import React, { useState } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Plus, Minus, Clock, Bell, BellRing, Flame, CalendarClock, CheckCircle2, Gift } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { restockNotify } from "@/lib/api";
import { toast } from "sonner";

const fireConfetti = () => {
  confetti({
    particleCount: 70,
    spread: 70,
    origin: { y: 0.7 },
    colors: ["#D7263D", "#1F9D55", "#FFD700", "#FFFFFF"],
    scalar: 0.9,
  });
};

export const ProductCard = ({ product, sizes, colors, index }) => {
  const { addItem } = useCart();
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [restockEmail, setRestockEmail] = useState("");
  const [restockDone, setRestockDone] = useState(false);
  const [restockSubmitting, setRestockSubmitting] = useState(false);
  const [giftNote, setGiftNote] = useState("");
  const isMerch = product.category === "merch";
  const isBouquet = product.made_to_order === true;
  const isPreorder = product.preorder === true;
  const isClearance = product.clearance === true;
  const needsSize = product.sized;
  const needsColor = Array.isArray(product.colors) && product.colors.length > 0;
  const comingSoon = product.coming_soon === true;
  const soldOut = !isMerch && !comingSoon && (product.stock_status === "out" || product.in_stock === false);
  const lowStock = product.stock_status === "low";

  const submitRestock = async (e) => {
    e.preventDefault();
    if (!restockEmail || !/\S+@\S+\.\S+/.test(restockEmail)) {
      toast.error("Enter a valid email, bandit!");
      return;
    }
    setRestockSubmitting(true);
    try {
      await restockNotify(product.id, restockEmail);
      setRestockDone(true);
      setRestockEmail("");
      toast.success("Got it! We'll email you the moment it's back. 🔔");
    } catch {
      toast.error("Something went wrong — try again.");
    } finally {
      setRestockSubmitting(false);
    }
  };

  const handleAdd = () => {
    if (needsColor && !color) {
      toast.error("Pick a color first!");
      return;
    }
    if (needsSize && !size) {
      toast.error("Pick a size first, bandit!");
      return;
    }
    addItem(
      product,
      needsSize ? size : null,
      qty,
      needsColor ? color : null,
      isBouquet && giftNote.trim() ? `\uD83C\uDF81 Gift note: ${giftNote.trim()}` : null
    );
    fireConfetti();
    toast.success(`${qty}× ${product.name} added to cart`);
    setQty(1);
    setGiftNote("");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.06 }}
      className="group relative spooky-card border-4 border-candy-pink rounded-2xl overflow-hidden shadow-brutal hover:-translate-y-2 hover:shadow-glow-orange transition-all duration-300 flex flex-col h-full"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden border-b-4 border-candy-pink p-3 bg-candy-night">
        <div className="relative rounded-xl overflow-hidden bg-white product-glow shimmer-overlay">
          <img
            src={product.image}
            alt={product.name}
            className={`h-56 w-full object-cover transition-transform duration-500 group-hover:scale-110 ${soldOut ? "grayscale opacity-70" : ""}`}
          />
          <div className="pointer-events-none absolute inset-0 rounded-xl" style={{ boxShadow: "inset 0 0 26px 12px rgba(6,23,13,0.35)" }} />
          <div className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-t from-candy-night/25 via-transparent to-candy-teal/5" />
        </div>
        {soldOut && (
          <span
            data-testid={`sold-out-badge-${product.id}`}
            className="absolute top-4 right-4 bg-black text-white border-2 border-white rounded-full px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider"
          >
            Sold Out
          </span>
        )}
        {!soldOut && isClearance && (
          <span
            data-testid={`clearance-badge-${product.id}`}
            className="absolute top-4 left-4 bg-[#FF1744] text-white border-2 border-black rounded-full px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider flex items-center gap-1 animate-pulse"
          >
            <Flame className="h-3 w-3" /> Clearance
          </span>
        )}
        {!soldOut && isPreorder && (
          <span
            data-testid={`preorder-badge-${product.id}`}
            className="absolute top-4 left-4 bg-candy-teal text-white border-2 border-black rounded-full px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider flex items-center gap-1"
          >
            <CalendarClock className="h-3 w-3" /> Pre-Order
          </span>
        )}
        {!soldOut && lowStock && (
          <span
            data-testid={`low-stock-badge-${product.id}`}
            className="absolute top-4 right-4 bg-orange-500 text-white border-2 border-black rounded-full px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider animate-pulse"
          >
            Only a few left!
          </span>
        )}
        {isMerch && (
          <span data-testid={`available-badge-${product.id}`} className="absolute bottom-4 left-4 bg-candy-teal text-white border-2 border-black rounded-full px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Available Now
          </span>
        )}
        {comingSoon && (
          <span data-testid={`coming-soon-badge-${product.id}`} className="absolute top-4 right-4 bg-candy-pink text-white border-2 border-candy-gold rounded-full px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider shadow-glow-orange">
            Coming Soon
          </span>
        )}
        {product.mega && (
          <span data-testid={`mega-badge-${product.id}`} className="absolute top-4 left-4 bg-candy-gold text-black border-2 border-black rounded-full px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-wider flex items-center gap-1">
            <Gift className="h-3 w-3" /> Mega Bouquet
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading font-bold text-xl leading-tight text-candy-cream drop-shadow-[1px_1px_0_rgba(0,0,0,0.6)]">{product.name}</h3>
        {product.weight && (
          <span
            data-testid={`product-weight-${product.id}`}
            className="mt-1.5 inline-flex self-start items-center bg-candy-night border-2 border-candy-teal text-candy-cream rounded-full px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider"
          >
            {product.weight}
          </span>
        )}
        <p className="mt-1.5 text-sm text-candy-cream/70 font-medium flex-1">{product.description}</p>
        {isPreorder && product.available && (
          <p
            data-testid={`preorder-notice-${product.id}`}
            className="mt-2 flex items-start gap-1.5 text-[11px] font-bold text-candy-cream bg-candy-teal/20 border-2 border-candy-teal rounded-lg px-2.5 py-1.5 leading-snug"
          >
            <CalendarClock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Pre-order now — ships {product.available}.
          </p>
        )}
        {isClearance && (
          <p
            data-testid={`clearance-notice-${product.id}`}
            className="mt-2 flex items-start gap-1.5 text-[11px] font-bold text-[#FF1744] bg-[#FF1744]/15 border-2 border-[#FF1744] rounded-lg px-2.5 py-1.5 leading-snug"
          >
            <Flame className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Limited stock — once it's gone, it's gone!
          </p>
        )}
        {isClearance && !soldOut && typeof product.stock_qty === "number" && (
          <div className="mt-3" data-testid={`stock-meter-${product.id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-[#FF1744] animate-pulse">
                🔥 Only {product.stock_qty} left!
              </span>
              <span className="font-mono text-[10px] text-candy-cream/50">{product.stock_qty}/10</span>
            </div>
            <div className="h-2.5 w-full bg-candy-night border-2 border-black rounded-full overflow-hidden">
              <div
                data-testid={`stock-bar-${product.id}`}
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.max(6, Math.min(100, (product.stock_qty / 10) * 100))}%`,
                  background: "linear-gradient(90deg,#FF1744,#FF6B00)",
                }}
              />
            </div>
          </div>
        )}
        {isBouquet && !comingSoon && (
          <p
            data-testid={`bouquet-notice-${product.id}`}
            className="mt-2 flex items-start gap-1.5 text-[11px] font-bold text-candy-cream bg-candy-gold/20 border-2 border-candy-gold rounded-lg px-2.5 py-1.5 leading-snug"
          >
            <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Made to order — needs 48–72 hrs advance notice. You'll pick a date (3+ days out) at checkout.
          </p>
        )}
        {product.flavors && (
          <p data-testid={`product-flavors-${product.id}`} className="mt-2 text-xs font-medium text-candy-cream/80">
            <span className="font-mono uppercase tracking-wider text-candy-teal">Flavors: </span>
            {product.flavors}
          </p>
        )}

        {!soldOut && (needsColor || needsSize) && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            {needsColor && (
              <select
                data-testid={`color-select-${product.id}`}
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="brutal-select text-sm w-full"
              >
                <option value="">Color…</option>
                {product.colors.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
            {needsSize && (
              <select
                data-testid={`size-select-${product.id}`}
                value={size}
                onChange={(e) => setSize(e.target.value)}
                className="brutal-select text-sm w-full"
              >
                <option value="">Size…</option>
                {sizes.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {comingSoon ? (
          <div className="mt-4">
            <span data-testid={`price-${product.id}`} className="font-display text-3xl text-candy-gold">${product.price.toFixed(2)}</span>
            <button
              data-testid={`coming-soon-btn-${product.id}`}
              disabled
              className="mt-3 w-full bg-candy-night text-candy-cream font-extrabold uppercase text-sm tracking-wider py-3.5 px-4 border-4 border-candy-gold rounded-xl cursor-not-allowed flex items-center justify-center gap-2"
            >
              <CalendarClock className="h-5 w-5" /> Coming Soon · Opens Nov 1
            </button>
          </div>
        ) : soldOut ? (
          <div className="mt-4">
            <span className="font-display text-3xl text-candy-cream/40 line-through">${product.price.toFixed(2)}</span>
            {restockDone ? (
              <p
                data-testid={`restock-done-${product.id}`}
                className="mt-3 font-heading font-700 text-sm bg-candy-gold text-black border-4 border-black rounded-xl px-3 py-2.5 flex items-center gap-2"
              >
                <BellRing className="h-4 w-4 shrink-0" /> You're on the list — we'll email you!
              </p>
            ) : (
              <form onSubmit={submitRestock} className="mt-3 flex flex-col gap-2" data-testid={`restock-form-${product.id}`}>
                <input
                  data-testid={`restock-email-${product.id}`}
                  type="email"
                  value={restockEmail}
                  onChange={(e) => setRestockEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full bg-candy-night text-candy-cream border-4 border-candy-teal rounded-lg px-3 py-2.5 font-medium text-sm focus:outline-none focus:shadow-brutal-teal transition-shadow placeholder:text-candy-cream/40"
                />
                <button
                  data-testid={`restock-notify-btn-${product.id}`}
                  type="submit"
                  disabled={restockSubmitting}
                  className="w-full bg-candy-pink text-white font-bold uppercase text-xs tracking-wider py-2.5 px-4 border-4 border-black rounded-xl shadow-brutal-sm hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform duration-150 disabled:opacity-60 flex items-center justify-center gap-1.5"
                >
                  <Bell className="h-4 w-4" /> {restockSubmitting ? "Adding…" : "Email Me When It's Back"}
                </button>
              </form>
            )}
          </div>
        ) : (
          <>
            {isBouquet && (
              <div className="mt-4">
                <label className="font-mono text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5 text-candy-cream">
                  <span aria-hidden>🎁</span> Add a gift note <span className="text-candy-cream/40 normal-case tracking-normal">(optional)</span>
                </label>
                <textarea
                  data-testid={`gift-note-${product.id}`}
                  value={giftNote}
                  onChange={(e) => setGiftNote(e.target.value)}
                  rows={2}
                  maxLength={200}
                  placeholder="Merry Christmas! Love, ..."
                  className="mt-1 w-full bg-candy-night text-candy-cream border-4 border-candy-teal rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:shadow-brutal-teal transition-shadow resize-none placeholder:text-candy-cream/40"
                />
                <p className="font-mono text-[10px] text-candy-cream/50 mt-0.5">We'll include this note with your order 💌</p>
              </div>
            )}
            <div className="mt-4 flex items-center justify-between gap-3">
              <span data-testid={`price-${product.id}`} className="font-display text-3xl text-candy-gold">${product.price.toFixed(2)}</span>
              <div className="flex items-center border-4 border-candy-teal rounded-xl overflow-hidden">
                <button
                  data-testid={`qty-dec-${product.id}`}
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="px-2.5 py-2 bg-candy-night text-candy-cream hover:bg-candy-teal hover:text-white transition-colors"
                  aria-label="decrease quantity"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span data-testid={`qty-value-${product.id}`} className="px-3 font-bold text-sm min-w-[2rem] text-center text-candy-cream bg-candy-night">
                  {qty}
                </span>
                <button
                  data-testid={`qty-inc-${product.id}`}
                  onClick={() => setQty((q) => Math.min(99, q + 1))}
                  className="px-2.5 py-2 bg-candy-night text-candy-cream hover:bg-candy-teal hover:text-white transition-colors"
                  aria-label="increase quantity"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            <button
              data-testid={`add-to-cart-${product.id}`}
              onClick={handleAdd}
              style={{ background: "linear-gradient(90deg,#D7263D,#E8414F)" }}
              className="mt-3 w-full text-white font-extrabold uppercase text-sm sm:text-base tracking-wider py-3.5 px-4 border-4 border-black rounded-xl shadow-glow-orange hover:-translate-y-1 hover:shadow-[0_0_28px_rgba(215,38,61,0.85)] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all duration-150 flex items-center justify-center gap-2"
            >
              <Plus className="h-5 w-5" /> {isPreorder ? "Pre-Order Now" : "Add to Cart"}
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
};
